// Angular imports
import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy, inject } from '@angular/core';
import { Subject, combineLatest, takeUntil, of } from 'rxjs';
import { filter, switchMap, tap, catchError, take } from 'rxjs/operators';

// Project libraries
import {
  PlatformInfoService,
  ProfilesService,
  UserDataService,
  UserProfileService as SharedUserProfileService,
} from '@tgx/shared/services';
import {
  ProfileData,
  SelectorOrganization,
  UserData,
  UserProfileData,
  UserProfileUpdate,
} from '@tgx/shared/interfaces';

// Local imports
import { StepInfo } from '../../interface/steps-info';
import { Steps } from '../../enum/steps';
import { OnboardingService } from '../../services/onboarding.service';
import { seller_integration_type } from '@tgx/shared/enums';
import { Router } from '@angular/router';
import { orderBy } from 'lodash';

@Component({
  selector: 'onboarding-onboarding-company-home',
  templateUrl: './onboarding-company-home.component.html',
  styleUrls: ['./onboarding-company-home.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OnboardingCompanyHomeComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private profilesService = inject(ProfilesService);
  private userDataService = inject(UserDataService);
  private onboardingService = inject(OnboardingService);
  private platformInfoService = inject(PlatformInfoService);
  private sharedUserProfileService = inject(SharedUserProfileService);

  Steps = Steps;
  steps = new Map<Steps, StepInfo<Steps>>([
    [Steps.DETAILS, { actualStep: true, name: Steps.DETAILS, width: 18, circle: '--active' }],
    [Steps.COMPANY_CONTACTS, { actualStep: false, name: Steps.COMPANY_CONTACTS, width: 48, circle: '' }],
    [Steps.BILLING, { actualStep: false, name: Steps.BILLING, width: 78, circle: '' }],
    [Steps.REVIEW, { actualStep: false, name: Steps.REVIEW, width: 100, circle: '' }],
    [Steps.CONFIRM, { actualStep: false, name: Steps.CONFIRM, width: 100, circle: '', hidden: true }],
  ]);

  isLoading = false;

  isBuyer = false;
  isSeller = false;
  isSellerPush = false;
  loggedUser: UserData;
  profile: ProfileData;
  jobTitle: string;
  companyName: string;
  userImg: string;
  selectedOrg: SelectorOrganization;
  protected orgMembersProfiles: UserProfileData[] = [];

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.isLoading = true;

    // Observable for early redirect if the user has finished onboarding
    // with take(1) it runs only once when the component initializes
    // to avoid redirecting while the user is in the onboarding process and to allow displaying
    // the final message and the button to start the onboarding implementation.
    this.platformInfoService.userLogged$
      .pipe(
        take(1), // Take only the first emission
        takeUntil(this.destroy$),
        filter(
          (userData) =>
            !!userData && (userData.onboardingProperties === null || !!userData.onboardingProperties?.finishedAt), // Ensure boolean
        ),
        tap(() => {
          // Redirect to dashboard if onboarding is finished
          this.router.navigate(['dashboard']);
        }),
      )
      .subscribe();

    // Main logic: only if user and organization are present and user has NOT finished onboarding
    combineLatest([this.platformInfoService.userLogged$, this.platformInfoService.organizationSelected$])
      .pipe(
        takeUntil(this.destroy$),
        filter(
          ([userData, selectedOrg]) =>
            !!userData &&
            !!selectedOrg &&
            !(userData.onboardingProperties === null || !!userData.onboardingProperties?.finishedAt), // Ensure boolean
        ),
        tap(([userData]) => {
          // Check and set the current onboarding step based on user data
          this.checkActualPositionStep(userData);
        }),
        switchMap(([userData, selectedOrg]) => {
          // Set main data for the component (user, organization, etc.)
          this.loggedUser = userData;
          this.selectedOrg = selectedOrg;
          this.userImg = userData.picture;
          this.companyName = selectedOrg.label;
          this.jobTitle = userData.partnersIncludedIn.find(
            (partner) => partner.organization.code === selectedOrg.code,
          )?.jobTitle;

          // Fetch profile and org members in parallel
          return (
            Promise.all([this.profilesService.getAllProfiles({ ownerCode: { equals: selectedOrg.code } })])
              .then(async ([profiles]) => {
                const orgMembersProfiles = await this.getOrgMembersProfiles(selectedOrg.code);
                return { profiles, orgMembersProfiles };
              })
              // Profiles come from CacheService, which already shows load failures; render the no-profile state.
              .catch(() => ({ profiles: [], orgMembersProfiles: [] }))
          );
        }),
        tap(({ profiles, orgMembersProfiles }) => {
          // Set profile and flags (isBuyer, isSeller, isSellerPush) based on fetched profile
          this.orgMembersProfiles = orderBy(orgMembersProfiles, 'firstName', 'asc');

          if (profiles.length > 0 && profiles[0].profileData) {
            this.profile = profiles[0].profileData;
            this.isBuyer = !!this.profile.buyerCategory;
            this.isSeller = !!this.profile.sellerCategory;
            this.isSellerPush =
              this.isSeller &&
              this.profile.sellerIntegrationType &&
              this.profile.sellerIntegrationType.some(
                (type) =>
                  type === seller_integration_type.HotelPush || type === seller_integration_type.HotelPushGeneric,
              );
          } else {
            // Reset flags if no profile found
            this.profile = undefined;
            this.isBuyer = false;
            this.isSeller = false;
            this.isSellerPush = false;
          }
          // Loading finished after profile is processed
          this.isLoading = false;
        }),
        catchError(() => {
          // Error handling if profile request fails
          this.profile = undefined;
          this.orgMembersProfiles = [];
          this.isBuyer = false;
          this.isSeller = false;
          this.isSellerPush = false;
          this.isLoading = false;
          return of();
        }),
      )
      .subscribe();
  }

  private async getOrgMembersProfiles(orgCode: string): Promise<UserProfileData[]> {
    if (!orgCode) {
      return [];
    }

    try {
      return await this.sharedUserProfileService.getUserProfilesDataByOrgCode(orgCode);
    } catch {
      // Already logged by GqlService; members list is optional for the onboarding flow.
      return [];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkActualPositionStep(userData: UserData): void {
    const props = userData.onboardingProperties;
    if (!props?.professionalDetailsFinishedAt) {
      this.changeStep(Steps.DETAILS);
    } else if (!props?.companyContactsFinishedAt) {
      this.changeStep(Steps.COMPANY_CONTACTS);
    } else if (!props?.billingDataFinishedAt) {
      this.changeStep(Steps.BILLING);
    } else if (!props?.finishedAt) {
      this.changeStep(Steps.REVIEW);
    } else {
      this.changeStep(Steps.CONFIRM);
    }
  }

  async updateUserProfile(data: UserProfileUpdate) {
    await this.userDataService.updateUserProfile(data);
  }

  changeStep(currentStep: Steps): void {
    const newSteps = new Map<Steps, StepInfo<Steps>>();
    const isEditing = this.onboardingService.getIsEditState();
    let matchCurrentStep = false;

    this.steps.forEach((step, key) => {
      let status: string;

      if (key === currentStep) {
        status = '--active';
        matchCurrentStep = true;
      } else {
        if (isEditing) {
          status = '--success';
        } else {
          status = !matchCurrentStep ? '--success' : '';
        }
      }

      const updateStep: StepInfo<Steps> = {
        ...step,
        actualStep: key === currentStep,
        circle: status,
        width: isEditing ? 100 : step.width,
      };
      newSteps.set(key, updateStep);
    });
    this.steps = newSteps;
  }
}
